import { createRootRoute, createRoute, Link, Navigate, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useMe } from "../../lib/me";
import { PageHeader } from "../../ui";
import { AppLayout } from "./AppLayout";
import { rememberWorkspace, useCurrentWorkspaceId } from "./current-workspace";

/**
 * The shell's routes. Feature folders hang their own routes off `workspaceRoute` (per-workspace
 * screens) or `appRoute` (everything else); `src/router.tsx` only assembles the tree.
 */
export const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

export const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "app", component: AppLayout });

export const indexRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: Home });

export const workspaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "w/$workspaceId",
  component: WorkspaceOutlet,
});

function Home() {
  const workspaceId = useCurrentWorkspaceId();
  if (workspaceId === null) return <NotFound />;
  return <Navigate to="/w/$workspaceId/dashboard" params={{ workspaceId }} replace />;
}

function WorkspaceOutlet() {
  const { workspaceId } = workspaceRoute.useParams();
  const me = useMe();
  const known = me.workspaces.some((w) => w.id === workspaceId);
  useEffect(() => {
    if (known) rememberWorkspace(workspaceId);
  }, [known, workspaceId]);
  if (!known) {
    return (
      <div>
        <PageHeader title="Workspace not found" />
        <p className="text-sm text-muted">
          It does not exist or you are not a member.{" "}
          <Link to="/" className="text-accent underline">
            Go to your workspaces
          </Link>
        </p>
      </div>
    );
  }
  return <Outlet />;
}

function NotFound() {
  return (
    <div className="p-6">
      <PageHeader title="Page not found" />
      <Link to="/" className="text-sm text-accent underline">
        Back to KitWork+
      </Link>
    </div>
  );
}
